<script lang="ts">
	/**
	 * `/Product/:slug` — the product detail page (Design §14). **Public**, and the page the
	 * anonymous-browsing rework exists for: a first-time visitor following a shared link must land
	 * here and see the product, not the identity provider.
	 *
	 * **The page is the checkout, and there is no Buy button.** The three steps — who is buying,
	 * where they are buying from, and what they are agreeing to — sit under the product's own
	 * description from the moment the page loads, with the money in the rail as an order summary.
	 *
	 * There was a Buy button, and before that a confirmation popup, and each was a door in front of
	 * a room the customer was already standing in. The popup was the wrong shape twice over: it
	 * arrived *after* the decision, over a dimmed copy of the thing being decided, and it had to
	 * hold a sign-in, §10.1's billing question, the terms and the options in a window sized for a
	 * receipt. Replacing it with a button that revealed the same steps in place fixed the second
	 * complaint and not the first — the customer still had to ask for a checkout while looking at
	 * the thing they had already chosen to buy.
	 *
	 * The step order is what earns the merge: the email comes first because until the buyer is
	 * known the store cannot say what else it needs — the country question is about *that customer*
	 * — and the terms come last because they are what the pay button commits. **A step commits only
	 * itself**, so nothing below one may disable what is above it; the deadlock that rule is named
	 * after is in `CheckoutSteps`.
	 *
	 * The commit is disabled with "not open yet" when `configuration.isOpen` is false — a control
	 * that 500s teaches customers the store is broken rather than that it has not launched.
	 *
	 * **This page is the one place the store commits money**, whichever direction it moves.
	 * A purchase, a move between tiers and a change of seat count all happen here, through the same
	 * flow. They used to be two screens: the ladder sold from here and `/Account/Subscription/:uuid`
	 * ran preview-and-confirm for changes, so a customer moving *up* a line was quoted by a screen
	 * they reached from a different page, in a different layout, with different words for the same
	 * decision — and the tier they were moving to was described on the page they had just left.
	 * The subscription page keeps its Change and Change-users controls; both now link here. A change
	 * asks neither for an email nor for a billing identity: it acts on a subscription that already
	 * exists, whose customer answered both when they bought it.
	 *
	 * A change is **not** a purchase and does not pretend to be one: it takes no checkout intent
	 * and never reaches `/Checkout/Start`. It quotes the server's proration, applies through
	 * `changeSubscriptionPlan` / `changeSubscriptionSeats`, and hands off to the bank only when an
	 * SCA challenge says so — the one branch where nothing has been charged and nothing has moved,
	 * so it must leave rather than report success.
	 *
	 * The layout is two columns with a **sticky purchase rail**. The rail is the decision and the
	 * left column is the argument for it; on a long description the two used to scroll apart, and
	 * the customer finished reading the case for buying with no way to act on it in view.
	 *
	 * **Money is rendered once per fact.** Every figure on this page comes from a `PriceDisplay`
	 * that owns exactly one number — the price cards, the coupon quote, the seat total — and the
	 * itemised arithmetic lives in `OrderSummary` rather than being restated here. Two places
	 * rendering the same amount is how they drift, and a duplicated figure also breaks `getByText`,
	 * which throws on multiple matches.
	 */

	import { onMount, tick } from 'svelte';
	import { Badge, Breadcrumb, Button } from 'flowbite-svelte';
	import { push } from '../../svelte-spa-router';
	import {
		CouponField,
		CurrencySelector,
		EmptyState,
		PageHeader,
		Panel,
		PriceDisplay,
		CheckoutSteps,
		OrderSummary,
		RoutedBreadcrumbItem,
		RoutedLink,
		SkeletonWrapper,
		SeatSelector,
		ProductPageSkeleton
	} from '../../components';
	import { foldDuration } from '../../components/Panel.svelte';
	import {
		changeSubscriptionPlan,
		changeSubscriptionSeats,
		conditionOf,
		getCatalogProduct,
		listCatalogProducts,
		previewSeatChange,
		previewSubscriptionChange
	} from '../../AppNetwork';
	import { setAnalyticsContext, track } from '../../stores/analytics';
	import { toastError, toastSuccess } from '../../stores/toast';
	import { configurationStore, reloadConfiguration } from '../../AppConfiguration';
	import { billingPeriodName, featureSectionsOf, mediaURL } from '../../Utilities';
	import {
		activeCurrency,
		activeCurrencyCode,
		availableCurrencies,
		pinnedCurrency,
		selectCurrency
	} from '../../stores/storefront';
	import { writeIntent } from '../../stores/checkoutIntent';
	import AbstractID, { isAuthorized } from '../../AbstractID';
	import type {
		Clickwrap,
		Configuration,
		CouponValidation,
		Price,
		Product,
		ProductOption,
		ProrationPreview
	} from '../../AppTypes';

	/**
	 * A discount code the customer arrived with: `/Product/:slug?coupon=SUMMER` (§4.1).
	 *
	 * It is **read once and taken straight back out of the address bar**, and that is not tidiness.
	 * `AbstractID.beginSignIn()` with no argument returns to `document.location.href`, and
	 * `makeReturnURL` strips only the hash and the token — so a code left in the query would be
	 * handed to the identity provider as a `returnURL` parameter and logged there, which is the
	 * exact reason §14 puts only a nonce in the checkout URL. Reading it into memory and clearing
	 * the address closes that off for every sign-in path at once, including ones added later.
	 *
	 * `replaceState` rather than `push`, because arriving on a link is not a navigation the customer
	 * should be able to go "back" through into a URL that no longer means anything.
	 */
	let arrivingCoupon: string = '';

	onMount(() => {
		const parameters = new URLSearchParams(window.location.search);
		const code = (parameters.get('coupon') ?? '').trim();
		if (code.length === 0) return;
		// Bounded here as well as server-side: the field caps a typed code at 60 characters and a
		// link is no reason to relax it.
		arrivingCoupon = code.slice(0, 60);
		parameters.delete('coupon');
		const query = parameters.toString();
		window.history.replaceState(
			{},
			'',
			`${window.location.pathname}${query.length > 0 ? `?${query}` : ''}`
		);
	});

	export let params: { slug?: string } = {};

	let product: Product | null = null;
	let loading: boolean = true;
	let loadError: string = '';
	let notFound: boolean = false;
	let selectedPriceUUID: string = '';
	let coupon: { code: string; validation: CouponValidation } | null = null;
	/** The other tiers in the line, for the switcher. Decoration: a failure costs the strip. */
	let siblings: Product[] = [];
	/**
	 * The checkout block, so the rail can send the customer to it.
	 *
	 * A binding rather than a `getElementById`: the id would be a second name for the same node,
	 * and a page that renders the section conditionally would leave the jump pointing at nothing.
	 */
	let checkoutSection: HTMLElement | null = null;
	/**
	 * Whether the two reading sections are collapsed, which the jump control closes on its way past.
	 *
	 * State on the page rather than inside the panels, because it is set from somewhere else: the
	 * customer who has pressed the jump has finished with the argument for buying, and leaving a
	 * screenful of it open between them and the steps is scrolling past the part they have already
	 * read. Both stay ordinary panels, so re-opening one is the same click it always was.
	 */
	let aboutHidden: boolean = false;
	let featuresHidden: boolean = false;

	$: slug = params.slug ?? '';
	$: isOpen = $configurationStore?.isOpen ?? false;
	$: prices = product?.prices ?? [];
	$: selectedPrice = prices.find(price => price.uuid === selectedPriceUUID) ?? prices[0] ?? null;
	$: highlight = product?.highlight ?? null;

	/**
	 * What this tier gives, in the sections it was written in (§14).
	 *
	 * The same `comparisonFeatures` the tier table is drawn from, and the only such field — a
	 * separate list of marketing bullets used to sit beside it, and the two could not agree by
	 * construction: whichever an admin edited, the other went stale, and the storefront rendered
	 * both. An ungrouped run keeps a `null` title and renders with no heading, so a line that never
	 * bothered with sections still reads as a plain list.
	 */
	$: featureSections = featureSectionsOf(product?.comparisonFeatures);

	/**
	 * Seats for the selected price (§9.7).
	 *
	 * Reset to the new price's floor whenever the selection changes: the tiers in a family may
	 * differ in their bounds, and carrying a count of forty onto a plan that caps at ten would
	 * offer the customer a purchase the server refuses.
	 */
	let seats: number = 1;
	$: if (selectedPrice !== null) {
		seats = selectedPrice.isSeatBased ? Math.max(seats, selectedPrice.seatMin) : 1;
	}
	let lastSeatPriceUUID: string = '';
	$: if (selectedPrice !== null && selectedPrice.uuid !== lastSeatPriceUUID) {
		lastSeatPriceUUID = selectedPrice.uuid;
		seats = startingSeats(selectedPrice);
	}

	/**
	 * What one of these is, in words (§4.1).
	 *
	 * The same control sells two different things: seats on a subscription, and units of a one-time
	 * product sold by the unit — a credit pack, an extra slot. The arithmetic is identical and the
	 * nouns are not, and "2 users" on the purchase of two credit packs is wrong on the one screen a
	 * customer reads before paying.
	 */
	$: unitWords =
		product?.type === 'one_time'
			? { label: 'Quantity', icon: 'fa-layer-group', singular: 'item', plural: 'items' }
			: { label: 'Users', icon: 'fa-users', singular: 'user', plural: 'users' };

	/**
	 * Where the seat control opens.
	 *
	 * The team the customer already has, when the server told us — clamped into the tier's own
	 * bounds, because the tiers in a line may differ in theirs. Opening at the floor instead would
	 * tell a customer with forty users that they have five, on the very screen where they are
	 * deciding what to pay for; and carrying forty onto a tier that caps at ten would offer a
	 * purchase the server refuses.
	 */
	function startingSeats(price: Price): number {
		if (!price.isSeatBased) return 1;
		const held = typeof price.heldSeats === 'number' ? price.heldSeats : null;
		const floor = Math.max(price.seatMin, 1);
		const wanted = Math.max(held ?? floor, floor);
		return price.seatMax === null ? wanted : Math.min(wanted, Math.max(price.seatMax, floor));
	}

	async function fnLoad(productSlug: string, currencyCode: string): Promise<void> {
		if (productSlug.length === 0) return;

		loading = true;
		loadError = '';
		notFound = false;
		coupon = null;
		siblings = [];
		try {
			product = await getCatalogProduct(productSlug, { currency: currencyCode || undefined });
			// Default to the price the admin marked default for its interval; the server already
			// sorts it first, so this is a re-selection rather than a second policy.
			selectedPriceUUID = (product.prices.find(price => price.isDefault) ?? product.prices[0])?.uuid ?? '';
			/*
			 * §12.2's second funnel stage, and the denominator of every per-product conversion rate.
			 *
			 * Recorded **after** the read succeeds, not on arrival: a slug that 404s is not a product
			 * view, and counting it would put a conversion rate of zero against a product that does
			 * not exist — which reads as a product nobody wants.
			 */
			track('product_view', {
				path: `/Product/${productSlug}`,
				productSlug,
				familySlug: product.familySlug ?? undefined,
				priceUUID: selectedPriceUUID.length > 0 ? selectedPriceUUID : undefined,
				currency: currencyCode || undefined
			});
			void fnLoadSiblings(product, currencyCode);
		} catch (error) {
			const message = (error as Error).message;
			if (/not found/i.test(message)) {
				notFound = true;
			} else {
				loadError = message;
			}
		} finally {
			loading = false;
		}
	}

	/**
	 * The rest of the line, for the tier strip.
	 *
	 * Swallowed on failure and never surfaced as an error: the product is the page, and a customer
	 * who can read and buy this tier must not be shown a failure state because the sibling list
	 * could not be fetched.
	 */
	async function fnLoadSiblings(current: Product, currencyCode: string): Promise<void> {
		if (current.familySlug === null) return;
		try {
			const result = await listCatalogProducts({
				familySlug: current.familySlug,
				currency: currencyCode || undefined,
				limit: 100
			});
			siblings = result.items.sort((left, right) => left.rank - right.rank);
		} catch {
			siblings = [];
		}
	}

	$: void fnLoad(slug, $activeCurrencyCode);

	/**
	 * What a yearly price saves against paying monthly, as a percentage.
	 *
	 * A percentage rather than an amount, deliberately: the amount is already on the card, and a
	 * second money figure for the same price is one more number that can drift from the first.
	 * Null whenever there is nothing honest to claim — no monthly price to compare against, an
	 * amount missing in this currency, or a year that is not actually cheaper.
	 */
	$: monthlyReference =
		prices.find(price => price.interval === 'month' && price.intervalCount === 1 && price.amount !== null) ?? null;

	function savingsPercent(price: Price): number | null {
		if (
			monthlyReference === null ||
			price.uuid === monthlyReference.uuid ||
			price.interval !== 'year' ||
			price.intervalCount !== 1 ||
			price.amount === null ||
			monthlyReference.amount === null
		) {
			return null;
		}
		const twelveMonths = monthlyReference.amount * 12;
		if (twelveMonths <= 0 || price.amount >= twelveMonths) return null;
		return Math.round(((twelveMonths - price.amount) / twelveMonths) * 100);
	}

	/** The confirmation window (§14) — a purchase, a tier change or a seat change. */
	/**
	/**
	 * §10.1's question, asked **in the confirmation window** rather than on the way out of it.
	 *
	 * The server answers it in advance on `/api/configuration`, per caller: true only where the
	 * store works out its own tax *and* holds no country for this customer, which is a first
	 * purchase and nothing else. Asked here, the field sits beside the plan and the price it is
	 * about; asked on the far side of the hand-off it stood alone on a page showing neither, which
	 * reads as a form appearing for no reason.
	 *
	 * Only ever on a purchase: a plan or seat change acts on a subscription that already exists,
	 * whose customer was asked this when they bought it.
	 */
	$: needsCountry = confirmMode === 'buy' && $configurationStore?.billingCountryRequired === true;
	let billingCountry: string = '';
	/**
	 * The company the purchase is being made by, asked beside the country and optional there.
	 *
	 * It rides on `needsCountry` rather than a flag of its own: the two are one question — the
	 * country picks the jurisdiction, this picks which of its two rates applies (§10.1) — and a
	 * store that lets the provider compute tax asks neither.
	 */
	let billingCompany: string = '';
	/** §10.1 — the professional declaration; seeded from the one on file. */
	let billingProfessional: boolean = false;
	let countryError: string = '';

	/**
	 * Seeded from what the store already holds for this customer (§10.1), never asked blank.
	 *
	 * Mostly a no-op for the country here — `billingCountryRequired` goes false the moment one is
	 * on file, so the field showing usually means there is nothing to prefill. It earns its keep on
	 * the company, which is optional and therefore never suppresses the question: a customer who
	 * has bought as a business before and sees the box empty reads that as the store having
	 * forgotten, and retyping it is the only way to find out otherwise. It also covers the case
	 * where the two disagree — the flag was read anonymously and the re-read after sign-in found a
	 * company but no country.
	 *
	 * Once only, and only into an empty field: what the customer typed in this window is a later
	 * answer than anything on file, and a configuration reload must not overwrite it or refill a
	 * box they deliberately cleared. The re-read on `signedin` is exactly such a reload.
	 */
	let identitySeeded: boolean = false;

	function fnSeedBillingIdentity(config: Configuration | null): void {
		if (config === null || identitySeeded) return;

		/*
		 * Nothing to offer is **not** a seeding, and the difference is the whole of this page's
		 * case: the configuration this page boots with was read *anonymously*, so it carries null
		 * for both. Marking that as seeded would spend the one shot on the read that knows nothing
		 * and leave the re-read after sign-in — the first one that knows who is buying — unable to
		 * fill anything in.
		 */
		const country = config.billingCountry ?? '';
		const company = config.billingCompany ?? '';
		if (country.length === 0 && company.length === 0) return;

		identitySeeded = true;
		if (billingCountry.length === 0) billingCountry = country;
		if (billingCompany.length === 0) billingCompany = company;
		billingProfessional = billingProfessional || config.billingProfessional === true;
	}

	$: professionalCountries = $configurationStore?.professionalCountries ?? [];
	/** Whether the box is on screen — the only case its answer is sent. */
	$: professionalOffered =
		billingCountry.length > 0 &&
		professionalCountries.includes(billingCountry.toUpperCase()) &&
		billingCompany.trim().length === 0;

	$: fnSeedBillingIdentity($configurationStore);
	/** Which of the three the open window is confirming. */
	/**
	 * Which of the three the flow is committing, **derived from the rail rather than fixed at Buy**.
	 *
	 * The rail stays live inside the checkout, so the customer can move from a tier they do not
	 * hold to the one they do — or from monthly to yearly — after the questions have started. That
	 * changes what pressing the button *does*: a purchase becomes a plan change, and a plan change
	 * becomes a seat change. Captured once at Buy it went stale the moment they touched the rail,
	 * and the flow would have applied the wrong operation with the right words on the button.
	 */
	$: confirmMode = (
		isSeatChange(selectedPrice)
			? 'seats'
			: isPlanChange(selectedPrice)
				? 'change'
				: isHeldPrice(selectedPrice)
					? 'held'
					: 'buy'
	) as 'buy' | 'change' | 'seats' | 'held';

	/**
	 * The mode the two flow components are given.
	 *
	 * `held` is a state of this page rather than a fourth thing either of them can render — one
	 * asks the purchase questions and the other totals what is being bought, and neither has
	 * anything to say about a plan that is not moving. Both are behind an `{#if}` that excludes it,
	 * so the fallback is never rendered; it exists so the union each component declares stays the
	 * three it actually handles.
	 */
	$: stepsMode = (confirmMode === 'held' ? 'buy' : confirmMode) as 'buy' | 'change' | 'seats';

	/** The subscription behind the price already held, for the link out to managing it. */
	$: heldSubscriptionUUID = confirmMode === 'held' ? subscriptionOf(selectedPrice) : null;
	let preview: ProrationPreview | null = null;
	let previewLoading: boolean = false;
	let previewError: string = '';
	let applying: boolean = false;

	/**
	 * The tier the customer is on today, named from the line the page already fetched.
	 *
	 * The server marks it with `action: 'current'` on one of the siblings, so no extra read is
	 * needed. It matters because the scheduled arm of a downgrade says "you keep **X** until 14
	 * September" — and naming the tier being *moved to* there would tell the customer they keep the
	 * thing they are giving up. Falls back to the neutral phrase rather than guessing.
	 */
	$: heldTierName =
		siblings.find(tier => tier.prices.some(candidate => candidate.action === 'current'))?.name ??
		'your current plan';

	/** The subscription a change acts on, when the server named one. */
	function subscriptionOf(price: Price | null): string | null {
		return price !== null && typeof price.subscriptionUUID === 'string' && price.subscriptionUUID !== ''
			? price.subscriptionUUID
			: null;
	}

	/**
	 * A move between two prices of the **same tier** — monthly to yearly, say.
	 *
	 * §9.3 does not rank two prices of one product, so the server settles the direction by amount
	 * and answers `upgrade` or `downgrade` like any other change. That is right for *how* it is
	 * applied — one prorates now, the other waits for the period end — and wrong for what it is
	 * called: nobody moving from monthly to yearly Pro is changing plan, and a button that says
	 * they are describes a move they did not ask for.
	 *
	 * Detected by observing that the server marked **another price on this same product** as the
	 * one held, which re-derives none of §9.3: the rank rule stays in `CatalogService`, and this
	 * only reads the answer it already gave.
	 */
	function isBillingChange(price: Price | null): boolean {
		return isPlanChange(price) && prices.some(candidate => candidate.action === 'current');
	}

	/** A move between tiers the server has already decided is a change rather than a purchase. */
	function isPlanChange(price: Price | null): boolean {
		return (
			price !== null &&
			(price.action === 'upgrade' || price.action === 'downgrade') &&
			subscriptionOf(price) !== null
		);
	}

	/**
	 * The server's quote for whatever the window is about to apply.
	 *
	 * Fetched when the window opens rather than on confirm: the figure **is** the step. Confirming
	 * before it arrives is agreeing to an amount nobody has been shown, and a refused preview is a
	 * refusal the server had a reason for — which the window renders instead of a button.
	 */
	async function fnPreview(): Promise<void> {
		const uuid = subscriptionOf(selectedPrice);
		if (selectedPrice === null || uuid === null) return;

		preview = null;
		previewError = '';
		previewLoading = true;
		try {
			preview =
				confirmMode === 'seats'
					? await previewSeatChange(uuid, seats)
					: await previewSubscriptionChange(
							uuid,
							selectedPrice.uuid,
							selectedPrice.isSeatBased ? seats : undefined
						);
		} catch (error) {
			previewError = (error as Error).message;
		} finally {
			previewLoading = false;
		}
	}

	/**
	 * Re-quotes whenever what would be applied changes.
	 *
	 * Keyed on a **string** rather than written as a statement reading the values directly, so the
	 * fetch runs once per distinct target: Svelte re-runs a reactive statement on every assignment
	 * to anything it read, and `fnPreview` writes `preview`, `previewLoading` and `previewError`,
	 * which is a loop that re-fetches for ever.
	 *
	 * With the checkout merged into the page there is no "window opens" moment to hang this on any
	 * more — the rail stays live throughout, so moving between tiers or dragging the seat count
	 * after the questions have started has to re-quote. Empty on a purchase: a purchase quotes
	 * itself from the catalogue and has no proration to ask about.
	 */
	// `=== change || === seats` rather than `!== buy`: a held price applies nothing, so there is
	// nothing to prorate, and asking the server to quote a change to the plan already in force
	// spends a round trip to be refused.
	$: quoteKey =
		confirmMode === 'change' || confirmMode === 'seats' ? `${selectedPrice?.uuid ?? ''}:${seats}` : '';
	$: if (quoteKey !== '') {
		void fnPreview();
	} else {
		preview = null;
		previewError = '';
	}

	/**
	 * Applies the change the window quoted.
	 *
	 * The SCA branch leaves the app rather than reporting success: until the customer completes the
	 * challenge nothing has been charged and the plan has not moved, so a success message here
	 * would be a claim about money that has not been taken. It goes through `AbstractID.navigateTo`
	 * like every other cross-origin redirect, which is also what makes it observable from a test.
	 */
	async function fnApplyChange(): Promise<void> {
		const uuid = subscriptionOf(selectedPrice);
		if (selectedPrice === null || uuid === null) return;

		applying = true;
		track('change_started', {
			productSlug: slug,
			priceUUID: selectedPrice.uuid,
			properties: { kind: confirmMode }
		});
		try {
			const result =
				confirmMode === 'seats'
					? await changeSubscriptionSeats(uuid, seats)
					: await changeSubscriptionPlan(
							uuid,
							selectedPrice.uuid,
							selectedPrice.isSeatBased ? seats : undefined,
							acceptedTermsPayload
						);

			if (result.kind === 'immediate' && result.status === 'requires_action' && result.requiresActionURL !== null) {
				AbstractID.navigateTo(result.requiresActionURL);
				return;
			}

			track('change_committed', {
				productSlug: slug,
				priceUUID: selectedPrice.uuid,
				properties: { kind: confirmMode, scheduled: result.kind === 'scheduled' }
			});
			toastSuccess(
				result.kind === 'scheduled'
					? 'Scheduled. Nothing changes until your next period.'
					: 'Done. Your subscription has been updated.'
			);
			// To the subscription, which is where the new state — and a scheduled change — is shown.
			push(`/Account/Subscription/${uuid}`);
		} catch (error) {
			// The condition, never the sentence: the sentence is free to change and cannot be
			// grouped by, which is the same reason `conditionOf` exists.
			track('error_shown', {
				path: `/Product/${slug}`,
				productSlug: slug,
				properties: { condition: conditionOf(error) || 'unknown' }
			});
			toastError(
				confirmMode === 'seats' ? 'Your users were not changed.' : 'Your plan was not changed.',
				(error as Error).message
			);
		} finally {
			applying = false;
		}
	}

	/**
	 * The intent-nonce hand-off of §14, once the customer has confirmed.
	 *
	 * The choice goes through `sessionStorage` rather than the URL because the coupon code must
	 * never travel in a query parameter the identity provider would log.
	 *
	 * There is now **one branch rather than two**: signed in or not, Buy routes to
	 * `/Checkout/Start`. The sign-in happens in the confirmation window itself, so an anonymous
	 * buyer is no longer sent to another origin between confirming a purchase and paying for one —
	 * and there is no second path that can drift away from the first.
	 */
	function fnPay(): void {
		// The window is shared, so the confirm it raises has to be routed the same way Buy was.
		if (confirmMode !== 'buy') {
			void fnApplyChange();
			return;
		}
		if (selectedPrice === null) return;

		const nonce = writeIntent({
			priceUUID: selectedPrice.uuid,
			// The version travels with each tick, so wording that changed while the customer was
			// reading is refused by the server rather than silently treated as agreed — and the
			// answer to a term that asked a question travels with it, since the box it was typed
			// into does not survive the sign-in this nonce exists to cross.
			acceptedClickwraps: acceptedTermsPayload,
			// §4.8, carried for the same reason: an option is *offered*, so an intent that lost one
			// is indistinguishable from a buyer who took none — and the customer would be charged
			// list price for an obligation they agreed to keep.
			selectedOptions: selectedOptionsPayload,
			couponCode: coupon?.code ?? null,
			currency: $activeCurrencyCode,
			productSlug: slug,
			// Carried across sign-in with everything else: a customer who chose forty seats and was
			// sent to sign in must not come back to one, and the number exists nowhere else.
			seats: selectedPrice.isSeatBased ? seats : null,
			// §10.1, answered in the window above. Empty unless this store had to ask, so a store
			// on the provider's own tax engine carries nothing extra.
			billingCountry,
			// Empty means "not a company", which is the common case and is what the server reads a
			// missing value as. It changes the rate, so it cannot wait for the billing page.
			billingCompany,
			// Only where the box was offered; null otherwise, so nothing on file is withdrawn.
			billingProfessional: professionalOffered ? billingProfessional : null
		});
		push(`/Checkout/Start?intent=${encodeURIComponent(nonce)}`);
	}

	/**
	 * The far side of the inline sign-in, which is the first moment the buyer is known.
	 *
	 * §10.1's question is about *that buyer*, and the answer the page booted with was about an
	 * anonymous one — the configuration is read `publicAuth` and resolves whoever is asking — so it
	 * is re-read here. That is what fills the billing step in with the country and company already
	 * on file instead of asking a returning customer for them again.
	 *
	 * **Signing in advances a step; it does not buy anything.** In the popup it *was* the
	 * confirmation, because a dialog had nowhere to carry on to and asking for a second press
	 * re-confirmed a decision made two screens earlier. Inline, the customer has not moved and the
	 * next question is already on screen, so the purchase is committed where it says it is: the
	 * button under the terms.
	 */
	async function fnAfterSignIn(): Promise<void> {
		// §12.2's `identified` stage. Emitted here rather than inside `SignInPanel`, which is shared
		// with `/Checkout/Start` and has no idea what is being bought.
		track('signin_completed', { productSlug: slug });
		// Swallowed, and deliberately: this is the courtesy of asking early, not the guard.
		// `createSession` refuses on its own when a country is genuinely missing and
		// `/Checkout/Start` asks there — so a configuration read that fails must cost the customer
		// one extra screen, never the purchase they have just signed in to make.
		await reloadConfiguration().catch(() => undefined);
	}

	/**
	 * The price the customer is **already on**, with nothing left for this page to do about it.
	 *
	 * It is the fourth thing the flow can be, and it was missing: `current` fell through to `buy`,
	 * so selecting the tier you hold — which is one click in the rail, and where the page lands by
	 * itself when the held tier is the default price — drew a full purchase headed "Complete your
	 * purchase", asked for an email, a billing country and the terms again, quoted "Due today
	 * $19.00" for money nobody was about to take, and ended at a commit `canCommit` disables for
	 * ever. Every part of that is a claim about a purchase that cannot happen.
	 *
	 * The seat and plan arms are tested **first**, because a held price is exactly what those two
	 * act on: a per-seat plan already held is a seat change, and a move off it is a plan change.
	 * This is what is left — the price itself, unchanged — and the answer to it is a sentence, not
	 * a form.
	 */
	function isHeldPrice(price: Price | null): boolean {
		return price !== null && price.action === 'current';
	}

	/** The plan already held, sold per seat, with a subscription to act on. */
	function isSeatChange(price: Price | null): boolean {
		return (
			price !== null &&
			price.action === 'current' &&
			price.isSeatBased &&
			typeof price.subscriptionUUID === 'string' &&
			price.subscriptionUUID !== ''
		);
	}

	/**
	 * The terms this product requires (§4.7), and which of them are ticked.
	 *
	 * Inline, beside Buy, rather than in the confirmation window: the customer is deciding to buy
	 * here, and the terms are part of that decision rather than a step after it. The window recaps
	 * what was ticked; it cannot itself agree to anything.
	 */
	$: clickwraps = product?.clickwraps ?? [];
	let ticked: Record<string, boolean> = {};

	/**
	 * What the customer typed into a term that asks a question (§4.7), keyed by term.
	 *
	 * Separate from `ticked` rather than folded into one record, because they answer different
	 * things: the tick is agreement and this is its subject. A term that asks nothing never appears
	 * here, and one that does is not accepted until both are present.
	 */
	let answers: Record<string, string> = {};

	/**
	 * The question a term asks, or null.
	 *
	 * `?? null` and a length test rather than a truthiness check on the field, because a server
	 * that predates the column sends `undefined` — which is not null to anything comparing against
	 * it, and would draw an unlabelled required text box under every term in the store.
	 */
	function questionOf(term: Clickwrap): string | null {
		const label = (term.inputLabel ?? '').trim();
		return label.length === 0 ? null : label;
	}

	/**
	 * What travels to the server, built once for both the purchase and the plan-change paths.
	 *
	 * One expression rather than two call sites building the same list, because the two are the
	 * same agreement reaching the server by different routes and a difference between them would
	 * be a term answered on a purchase and silently unanswered on an upgrade. The answer key is
	 * **omitted** where the term asks nothing: `undefined` becomes `null` through `JSON.stringify`,
	 * and the route schema takes a string or nothing at all.
	 */
	$: acceptedTermsPayload = clickwraps.map(term => {
		const answer = (answers[term.uuid] ?? '').trim();
		return questionOf(term) === null || answer.length === 0
			? { uuid: term.uuid, version: term.version }
			: { uuid: term.uuid, version: term.version, inputValue: answer };
	});

	/**
	 * The options this product offers (§4.8), and which of them are ticked.
	 *
	 * Beside the terms and rendered the same way, because to the customer they are the same shape
	 * of thing — words with a checkbox. The difference is the one word that runs through the whole
	 * feature: a term is required and gates the payment, an option is offered and gates nothing.
	 * Nothing here may ever appear in `canCommit`.
	 *
	 * Offered on a **purchase only**. A tier change keeps the subscription row, and the options
	 * ride on it — the discount stays applied at the provider and the keys stay on the entitlement
	 * — so re-asking here would offer a second copy of a decision already in force, and a seat
	 * change is not a new purchase at all.
	 */
	$: productOptions = product?.options ?? [];
	let optionTicked: Record<string, boolean> = {};

	$: chosenOptions = productOptions.filter(option => optionTicked[option.uuid] === true);

	/**
	 * The currencies **this product** can actually be bought in (§10).
	 *
	 * The store's enabled list is the right offer on the catalogue, where the customer is looking at
	 * many products, and the wrong one here. A price carries an amount per currency, so a plan sold
	 * only in euros on a store that also enables dollars and yen was offering two choices that lead
	 * nowhere: switching lands on `PriceDisplay`'s "unavailable", which is honest but is a dead end
	 * the customer was invited into. Narrowing the offer to what the product has amounts for means
	 * the single-currency product simply has no selector — the component drops it once there is
	 * nothing to choose.
	 *
	 * Built from `amounts`, which §10 ships for exactly this reason ("every currency this price
	 * carries, so a currency switch needs no refetch"), and then **intersected with the store's
	 * enabled list in the store's own order**. The intersection is not tidiness: disabling a currency
	 * stops new purchases in it while leaving old amounts on the price, so a currency the admin
	 * switched off last month is still on the row and must not be offered to a new buyer.
	 */
	$: productCurrencyCodes = new Set(
		prices.flatMap(price => (price.amounts ?? []).map(amount => amount.currency))
	);
	$: productCurrencies = $availableCurrencies.filter(currency =>
		productCurrencyCodes.has(currency.code)
	);

	/**
	 * A product sold in one currency **moves the page to it**.
	 *
	 * This is the other half of hiding the control, and without it hiding is worse than what it
	 * replaced: a customer whose currency is dollars, on a plan priced only in euros, would meet
	 * "Not available in USD" with the one control that could have fixed it now gone. Two halves of
	 * one decision — if there is nothing to choose, the page chooses.
	 *
	 * **Only where there is genuinely no choice.** §10 is explicit that a price with no amount in
	 * the requested currency renders as unavailable *rather than falling back*, because a silent
	 * fallback charges somebody in money they did not pick. That rule is about a store with options;
	 * it does not reach a product with exactly one, where "falling back" and "the only currency it
	 * is sold in" are the same thing. A product carrying two currencies and neither of them the
	 * customer's keeps saying unavailable, and keeps its selector to say it with.
	 *
	 * **The pin still wins.** `selectCurrency` refuses once `Customer.defaultCurrency` is set, so a
	 * customer billed in dollars is not moved onto a euro-only plan — checkout would answer that
	 * with a 409, and a page that walked them into it would be inventing a purchase the API refuses.
	 * They see the unavailable state, which is the truth: that product is not for sale to them.
	 */
	$: if (
		productCurrencies.length === 1 &&
		$activeCurrencyCode !== productCurrencies[0].code
	) {
		selectCurrency(productCurrencies[0].code);
	}

	/*
	 * §12.2 — what this page is about, for the events raised on it that do not know.
	 *
	 * `CheckoutSteps` is shared with the change flows and has no idea which product it is selling;
	 * `SignInPanel` knows less still. Without this every step, sign-in and coupon attempt on a
	 * product page arrives with a null product and a null currency, and the Products and Currency
	 * breakdowns report the lot as "Unknown" — which is the page's own information going missing
	 * rather than an absence anybody could act on.
	 *
	 * Reactive rather than set once, because the rail is live: the currency and the selected plan
	 * both move under the customer, and an event carrying the currency they started on is worse
	 * than one carrying none.
	 */
	$: if (product !== null) {
		setAnalyticsContext({
			productSlug: slug,
			...(product.familySlug !== null ? { familySlug: product.familySlug } : {}),
			...(selectedPrice !== null
				? { priceUUID: selectedPrice.uuid, currency: selectedPrice.currency }
				: {})
		});
	}

	/*
	 * §12.2's decision events — which rung, how many seats, which extras.
	 *
	 * All three are **bound** controls rather than handlers, so each is recorded by comparing
	 * against the last value reported rather than by hooking a click: `bind:` is what the rail
	 * writes through, and a handler beside it would miss the keyboard path and the reset that
	 * happens when the tier changes.
	 *
	 * The guards are plain variables, not reactive ones. A reactive flag written inside the block it
	 * guards is a loop, which is the same trap `quoteKey` exists to avoid on this page.
	 *
	 * Each is skipped on its first value: the page arrives with a default price, a seat floor and no
	 * options, and reporting those as choices would record a decision nobody made — inflating
	 * `plan_selected` to exactly one per product view.
	 */
	let reportedPriceUUID: string = '';
	$: if (selectedPrice !== null && selectedPrice.uuid !== reportedPriceUUID) {
		const first = reportedPriceUUID === '';
		reportedPriceUUID = selectedPrice.uuid;
		if (!first) {
			track('plan_selected', {
				productSlug: slug,
				priceUUID: selectedPrice.uuid,
				currency: selectedPrice.currency,
				properties: { interval: selectedPrice.interval, action: selectedPrice.action ?? 'none' }
			});
		}
	}

	let reportedSeats: number = 0;
	$: if (seats !== reportedSeats) {
		const first = reportedSeats === 0;
		reportedSeats = seats;
		if (!first) {
			track('seats_changed', { productSlug: slug, properties: { seats } });
		}
	}

	let reportedOptions: string = '';
	$: {
		const keys = chosenOptions.map(option => option.key).sort().join(',');
		if (keys !== reportedOptions) {
			const first = reportedOptions === '' && keys === '';
			reportedOptions = keys;
			if (!first) {
				// The **keys**, which are what ships in `entitlement.options` and what an integration
				// switches on — never the wording, which is editable and would go stale in the table.
				track('option_toggled', { productSlug: slug, properties: { options: keys } });
			}
		}
	}

	/**
	 * Whether an option takes money **off** rather than putting it on — the sign, in one place.
	 *
	 * Everything below turns on this, and getting it backwards would offer a customer a discount
	 * that charges them. A percentage reads its own value; a fixed amount reads the one for the
	 * currency being quoted, which is where its sign lives.
	 */
	function isDiscounting(option: ProductOption): boolean {
		return option.adjustmentType === 'percent'
			? (option.percentAdjustment ?? 0) < 0
			: option.adjustmentType === 'fixed' && (option.amount ?? 0) < 0;
	}

	/**
	 * Which kinds of **discount** are already ticked.
	 *
	 * A percentage and a fixed amount have no composition anybody chose — 10% off before €5 and 10%
	 * off after it are different numbers — so the server refuses the pair, and this is what stops
	 * the customer reaching that refusal at the payment page instead of on this one. Only discounts
	 * are in it: charges become line items rather than the one coupon, so they compose with
	 * anything and collide with nothing.
	 */
	$: chosenDiscountKinds = new Set(
		chosenOptions
			.filter(option => isDiscounting(option))
			.map(option => option.adjustmentType)
			.filter((kind): kind is NonNullable<ProductOption['adjustmentType']> => kind !== null)
	);

	/** Whether anything ticked actually costs the store money, which is what a coupon collides with. */
	$: optionDiscountChosen = chosenDiscountKinds.size > 0;

	/**
	 * What travels to the server, built once for the same reason `acceptedTermsPayload` is.
	 *
	 * The version rides with each tick: an option repriced while the customer was reading is
	 * refused rather than silently charged at the new figure.
	 */
	$: selectedOptionsPayload = chosenOptions.map(option => ({
		uuid: option.uuid,
		version: option.version
	}));

	/**
	 * Whether one option cannot be ticked right now, and it is never about the option itself.
	 *
	 * Three reasons, and each of them is a refusal the server would give: the store is shut, the
	 * option has no amount in this currency (§10 — shown and flagged, never hidden), a discount
	 * code is applied, or a discount of the other kind is already ticked. Disabling rather than
	 * hiding, so the customer can see the offer they are not currently eligible for and work out
	 * what to give up for it.
	 */
	function optionDisabled(option: ProductOption, applied: typeof coupon, kinds: Set<string>): boolean {
		if (!isOpen || option.unavailable) return true;
		// An option that **charges** collides with nothing: it becomes a line item rather than the
		// one coupon, so neither a discount code nor another discount can rule it out.
		if (!isDiscounting(option)) return false;
		if (applied !== null) return true;
		return kinds.size > 0 && !kinds.has(option.adjustmentType as string);
	}

	/**
	 * Why the coupon field is closed, or empty.
	 *
	 * The provider takes one discount per checkout, so a code and a discounting option cannot both
	 * apply — the server refuses the pair outright. Saying so here, where both controls are on
	 * screen together, is the difference between a choice the customer makes and a refusal they
	 * meet after pressing Buy.
	 */
	/**
	 * What the window recaps (§4.8) — nothing at all on a change.
	 *
	 * Empty there for the reason the coupon is: a change quotes the server's proration and
	 * re-decides neither, and the options already in force ride on the subscription rather than on
	 * the tier being moved to.
	 */
	$: confirmedOptions = confirmMode === 'buy' ? chosenOptions : [];

	/**
	 * What one option's disabled state should say, or nothing.
	 *
	 * Beside `optionDisabled` rather than inside it, because the two answer different questions —
	 * whether the box can be ticked, and why not — and a control that is disabled with no
	 * explanation reads as a broken page rather than as an offer with a condition on it.
	 */
	/**
	 * The disabled rule, bound to the state the steps cannot see.
	 *
	 * `optionDisabled` below takes the coupon and the ticked kinds explicitly so it stays a pure
	 * function of what it is given; this is the one that closes over them, because a template
	 * cannot carry a type annotation and an inline arrow there would be untyped.
	 */
	$: fnOptionDisabled = (option: ProductOption): boolean =>
		optionDisabled(option, coupon, chosenDiscountKinds);

	$: fnOptionNote = (option: ProductOption): string => {
		if (option.unavailable) {
			return `Not available in ${(selectedPrice?.currency ?? '').toUpperCase()}.`;
		}
		if (!isDiscounting(option)) return '';
		if (coupon !== null) return 'Remove the discount code to take this.';
		return chosenDiscountKinds.size > 0 &&
			!chosenDiscountKinds.has(option.adjustmentType as NonNullable<ProductOption['adjustmentType']>)
			? "Can't be combined with the discount already chosen."
			: '';
	};

	/**
	 * Whether the flow is ready to commit, which is only ever a question about a **change**.
	 *
	 * A change cannot be applied until its quote is on the screen: confirming before it arrives is
	 * agreeing to an amount nobody has been shown, and a refused preview is a refusal the server
	 * had a reason for. A purchase quotes itself from the catalogue, so it waits on nothing.
	 */
	/**
	 * Whether the flow can be committed at all.
	 *
	 * This was `canBuy` on a button that opened the checkout, and every condition in it was really
	 * about **committing** rather than about opening: a price sold in another currency, a store not
	 * taking purchases, a seat count that has not moved. With the checkout and the product now one
	 * page there is nothing to open, so they gate the thing that actually takes the money.
	 *
	 * The terms are deliberately **not** here — `CheckoutSteps` owns them, because it is what
	 * renders them and it is the one place that knows whether this mode asks for any.
	 */
	$: canCommit =
		isOpen &&
		selectedPrice !== null &&
		!selectedPrice.unavailable &&
		selectedPrice.action !== 'unavailable' &&
		// A plan already held is only actionable as a seat change, and only once the count moves —
		// "confirm" against the team already in force commits nothing and would report success.
		(selectedPrice.action !== 'current' || (isSeatChange(selectedPrice) && seatsDiffer)) &&
		// A change cannot be applied until its quote is on the screen: confirming before it arrives
		// is agreeing to an amount nobody has been shown, and a refused preview is a refusal the
		// server had a reason for. A purchase quotes itself from the catalogue and waits on nothing.
		(confirmMode === 'buy' || (preview !== null && !previewLoading && previewError.length === 0));

	/**
	 * What choosing this price would mean, in the server's own terms (§9.3).
	 *
	 * It used to be the Buy button's label. With the checkout merged into this page there is no
	 * such button, and the fact belongs on the **card** anyway: "upgrade" and "downgrade" are
	 * properties of a price relative to the plan held, so a customer comparing three rungs needs
	 * them on all three rather than on whichever one happens to be selected.
	 *
	 * The `action` discriminator is rendered and never re-derived — §9.3's rank rule lives in
	 * `CatalogService`, and a second implementation here would drift into offering an upgrade the
	 * API refuses. A billing change names **what** it changes and claims nothing about *when*: the
	 * server settles the direction by comparing a yearly total against a monthly one, so a switch
	 * to annual billing is `upgrade` on that arithmetic alone while the provider may still schedule
	 * it for the period end. The authority on the timing is the proration preview.
	 */
	function actionNote(price: Price): string {
		if (isSeatChange(price)) return '';
		if (isBillingChange(price)) return `Switch to ${billingPeriodName(price)}`;
		switch (price.action) {
			case 'upgrade':
				return 'Upgrade — prorated now';
			case 'downgrade':
				return 'Downgrade — starts at period end';
			case 'unavailable':
				return price.blockedReason === 'pendingChange'
					? 'A change is already scheduled'
					: 'Not available in this currency';
			default:
				return '';
		}
	}

	/**
	 * What the commit button says. Only this page knows whether it charges, schedules or pays.
	 *
	 * A reactive statement rather than a function called from the markup: it reads `preview`, which
	 * **arrives after the flow opens**, and Svelte re-runs a template call only when its arguments
	 * change. As a function it said "Confirm and pay now" on a downgrade for ever, because the
	 * quote that makes it a scheduled change landed after the last render that read it.
	 */
	$: commitLabel =
		confirmMode === 'seats'
			? 'Confirm user change'
			: confirmMode === 'change'
				? isBillingChange(selectedPrice)
					? `Switch to ${billingPeriodName(selectedPrice)}`
					: preview?.kind === 'scheduled'
						? 'Schedule this change'
						: 'Confirm and pay now'
				: selectedPrice !== null && selectedPrice.trialDays > 0
					? 'Start free trial'
					: 'Pay';

	$: couponBlockedReason = optionDiscountChosen
		? "A discount code can't be combined with the options you've chosen. Untick them to use a code."
		: '';

	/**
	 * What the jump control says, which is the heading it lands on.
	 *
	 * A button naming its destination is what keeps it a signpost rather than a second commit: the
	 * rail already holds the money, and two controls that both read as the end of a purchase is how
	 * the wrong one gets pressed. On a change it says what is being changed, for the same reason
	 * the heading below does — "Buy now" on a customer moving between tiers describes a purchase
	 * they are not making.
	 */
	$: jumpLabel =
		confirmMode === 'seats'
			? 'Change your users'
			: confirmMode === 'change'
				? isBillingChange(selectedPrice)
					? 'Change how often you pay'
					: 'Change your plan'
				: 'Buy now';

	/**
	 * Scroll the checkout into view and put the keyboard there with it.
	 *
	 * It is **never disabled**: it commits nothing, and a step below it may not disable what is
	 * above it — a store that is shut or a price sold in another currency is a sentence the steps
	 * themselves say, and hiding the way to read it leaves the customer with a refusal and nowhere
	 * to see why. Focus moves too, because a smooth scroll leaves a keyboard or screen-reader user
	 * exactly where they were, which is a control that appears to do nothing at all.
	 */
	async function fnGoToCheckout(): Promise<void> {
		const folding = !aboutHidden || !featuresHidden;
		aboutHidden = true;
		featuresHidden = true;

		// **After** the collapse has finished, never beside it: the two panels are above the
		// checkout, so closing them moves it up the page by their whole height. Scrolling against
		// the layout that is still on screen — or still sliding shut — lands the customer well
		// below what they asked for.
		await tick();
		if (folding) {
			await new Promise(resolve => setTimeout(resolve, foldDuration()));
		}

		if (checkoutSection === null) return;
		checkoutSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
		checkoutSection.focus({ preventScroll: true });
	}

	/** Whether the seat control has actually moved off the team in force. */
	$: seatsDiffer =
		selectedPrice !== null &&
		typeof selectedPrice.heldSeats === 'number' &&
		seats !== selectedPrice.heldSeats;
</script>

{#if loading}
	<!--
		A skeleton behind `SkeletonWrapper` rather than a spinner: the delay means a fast load — the
		ordinary case for a cached catalogue — shows nothing at all instead of a flash of grey.
	-->
	<SkeletonWrapper>
		<ProductPageSkeleton />
	</SkeletonWrapper>
{:else if notFound}
	<EmptyState title="No such product." description="The link may be out of date.">
		<Button color="alternative" size="sm" on:click={() => push('/')}>Browse the store</Button>
	</EmptyState>
{:else if loadError.length > 0}
	<EmptyState variant="error" title="The product could not be loaded." description={loadError}>
		<Button color="alternative" size="sm" on:click={() => fnLoad(slug, $activeCurrencyCode)}>Try again</Button>
	</EmptyState>
{:else if product !== null}
	<Breadcrumb class="mb-4">
		<RoutedBreadcrumbItem href="/" home>Store</RoutedBreadcrumbItem>
		{#if product.familySlug !== null}
			<RoutedBreadcrumbItem href={`/Products/${product.familySlug}`}>{product.familyName}</RoutedBreadcrumbItem>
		{/if}
		<RoutedBreadcrumbItem>{product.name}</RoutedBreadcrumbItem>
	</Breadcrumb>

	<!--
		The masthead. The logo sits beside the heading rather than above it so the name keeps the
		`PageHeader` underline every other page's title has, and the badges sit on the same line as
		the name because "Most popular" is a claim about *this product*, not about the page.
	-->
	<div class="flex items-start gap-4">
		{#if product.imageURL}
			<img src={mediaURL(product.imageURL)} alt="" class="w-16 h-16 rounded-2xl object-contain shrink-0 mt-1" />
		{/if}
		<div class="min-w-0 grow">
			<PageHeader title={product.name}>
				<svelte:fragment slot="description">
					{product.tagline ?? ''}
				</svelte:fragment>
			</PageHeader>
		</div>
	</div>

	<div class="flex flex-wrap items-center gap-2 mb-6 -mt-2">
		{#if highlight}
			<Badge color="blue" class="uppercase tracking-wide text-[10px]">
				<i class="fa-solid fa-star mr-1 text-[9px]" />{highlight}
			</Badge>
		{/if}
		<Badge color="dark">
			{#if product.type === 'subscription'}
				<i class="fa-solid fa-repeat mr-1 text-[10px]" />Subscription
			{:else}
				<i class="fa-solid fa-box mr-1 text-[10px]" />One-time purchase
			{/if}
		</Badge>
		{#if product.familySlug !== null}
			<RoutedLink
				to={`/Products/${product.familySlug}`}
				class="text-sm text-primary-500 hover:underline"
			>
				<i class="fa-solid fa-table-columns mr-1.5" />Compare every {product.familyName} tier
			</RoutedLink>
		{/if}
	</div>

	<div class="grid gap-6 lg:grid-cols-3 items-start">
		<div class="lg:col-span-2 space-y-6">
			<!--
				The tier strip. A ladder is only legible as a ladder when the rungs are visible from
				any one of them; the compare link alone made moving between tiers a two-page trip.
			-->
			{#if siblings.length > 1}
				<div class="flex flex-wrap gap-2">
					{#each siblings as tier (tier.uuid)}
						{#if tier.slug === product.slug}
							<span
								class="widget bg-depth border-widget !py-2 !px-3 text-sm font-semibold border-primary-500/50 flex items-center gap-2"
								aria-current="page"
							>
								{#if tier.imageURL}
									<img src={mediaURL(tier.imageURL)} alt="" class="w-5 h-5 rounded object-contain shrink-0" />
								{:else}
									<i class="fa-solid fa-circle-dot text-primary-500 text-xs" />
								{/if}
								{tier.name}
							</span>
						{:else}
							<RoutedLink
								to={`/Product/${tier.slug}`}
								class="widget bg-depth bg-depth-hover !py-2 !px-3 text-sm text-default hover:text-gray-900 dark:hover:text-white flex items-center gap-2"
							>
								{#if tier.imageURL}
									<img src={mediaURL(tier.imageURL)} alt="" class="w-5 h-5 rounded object-contain shrink-0" loading="lazy" />
								{/if}
								{tier.name}
								{#if tier.highlight}
									<i class="fa-solid fa-star text-primary-500 text-[10px]" title={tier.highlight} />
								{/if}
							</RoutedLink>
						{/if}
					{/each}
				</div>
			{/if}

			{#if product.description}
				<Panel title="About" faIcon="circle-info" animated bind:isHidden={aboutHidden}>
					<!-- `.richtext` restores what Tailwind preflight strips from authored HTML. -->
					<div class="richtext">{@html product.description}</div>
				</Panel>
			{/if}

			{#if featureSections.length > 0}
				<Panel title="What you get" faIcon="list-check" animated bind:isHidden={featuresHidden}>
					<div class="space-y-5">
						{#each featureSections as section, index (section.title ?? `__ungrouped-${index}`)}
							<div>
								{#if section.title !== null}
									<p class="card-label mb-2">{section.title}</p>
								{/if}
								<!--
									Two columns from `sm` up. A single column of short rows wastes the width
									the two-thirds layout just bought, and a long list scrolls the purchase
									rail out of reach for no reason.
								-->
								<ul class="grid gap-x-6 gap-y-2 sm:grid-cols-2">
									{#each section.rows as row (row.label)}
										<li class="flex items-start gap-2.5">
											<span
												class="mt-0.5 w-5 h-5 rounded-full bg-green-100 dark:bg-green-500/15 flex items-center justify-center shrink-0"
											>
												<i class="fa-solid fa-check text-green-600 dark:text-green-400 text-[10px]" />
											</span>
											<span class="text-sm">
												{row.label}{#if row.value !== null}<span class="text-default">: </span><span
														class="font-medium">{row.value}</span
													>{/if}
											</span>
										</li>
									{/each}
								</ul>
							</div>
						{/each}
					</div>
				</Panel>
			{/if}
			<!--
				The checkout, on the **same page as the product** (§14) — and there is no Buy
				button, because there is nothing left for one to open.

				It used to be a second state behind a click, which is one click and one page of
				context more than the decision needs: a customer who had decided had to press Buy
				to find out what the store would ask them for, and a customer still deciding could
				not see it at all. The left column now reads as one thing — here is the product,
				here is how to buy it — while the rail beside it stays the decision throughout:
				currency, plan and seat count, live, with the summary following whatever moves.
			-->
			<!-- `tabindex="-1"` so the rail's jump can land the keyboard here as well as the page. -->
			<div bind:this={checkoutSection} tabindex="-1" class="border-t border-widget-color pt-6 space-y-4 outline-none">
				<h2 class="text-lg font-semibold">
					{#if confirmMode === 'seats'}
						Change your users
					{:else if confirmMode === 'change'}
						<!--
							A billing change keeps the tier, so the heading may not call it a plan
							change — the customer is moving between monthly and yearly and would
							read that as being moved off what they pay for.
						-->
						{isBillingChange(selectedPrice) ? 'Change how often you pay' : 'Change your plan'}
					{:else if confirmMode === 'held'}
						This is your current plan
					{:else}
						Complete your purchase
					{/if}
				</h2>

				{#if confirmMode === 'held'}
					<!--
						Nothing to commit, so nothing that looks like a commit. The steps ask who is
						buying, where from and what they agree to — three questions with no answer
						here, and `CheckoutSteps` rendered them anyway because `current` fell through
						to `buy`. What the customer actually needs is the state they are in and the
						two ways out of it: choose something else in the rail, or manage what they
						hold.
					-->
					<p class="text-sm text-default">
						You're already subscribed on this plan and billing period, so there's nothing to
						buy here. Choose another tier or a different billing period to change it.
					</p>
					{#if heldSubscriptionUUID !== null}
						<RoutedLink
							to={`/Account/Subscription/${heldSubscriptionUUID}`}
							class="text-sm text-primary-500 hover:underline"
						>
							<i class="fa-solid fa-gear mr-1.5" />Manage this subscription
						</RoutedLink>
					{/if}
				{:else}
					<CheckoutSteps
						mode={stepsMode}
						isBillingChange={isBillingChange(selectedPrice)}
						isSignedIn={$isAuthorized}
						bind:billingCountry
						bind:billingCompany
						bind:billingProfessional
						{professionalCountries}
						countryRequired={needsCountry}
						{countryError}
						terms={clickwraps}
						bind:ticked
						bind:answers
						options={productOptions}
						bind:optionTicked
						optionDisabled={fnOptionDisabled}
						optionNote={fnOptionNote}
						currency={$activeCurrency}
						currencyCode={selectedPrice?.currency ?? ''}
						{isOpen}
						busy={applying}
						{canCommit}
						{commitLabel}
						on:signedin={fnAfterSignIn}
						on:pay={fnPay}
					/>
				{/if}
			</div>
		</div>

		<!--
			The purchase rail, pinned. The left column is the argument and this is the decision; on a
			long description the two scrolled apart, and the customer finished reading the case for
			buying with nothing to act on in view.
		-->
		<div class="space-y-4 lg:sticky lg:top-4">
			<div class="widget bg-depth shadow-widget space-y-4">
				<CurrencySelector
					currencies={productCurrencies}
					value={$activeCurrencyCode}
					pinnedCurrency={$pinnedCurrency}
					compact
					on:change={event => selectCurrency(event.detail)}
				/>

				{#if prices.length === 0}
					<EmptyState compact title="No price is published for this product." />
				{:else}
					<div class="space-y-2">
						<p class="card-label"><i class="fa-solid fa-tags text-[11px]" />Choose a plan</p>
						{#each prices as price (price.uuid)}
							{@const saving = savingsPercent(price)}
							<!--
								A real radio inside the card, not a click handler on a div: keyboard
								selection, arrow-key group traversal and the focus ring all come from
								the input, and all three are lost silently when it is replaced.
							-->
							<!-- svelte-ignore a11y-label-has-associated-control -->
							<label
								class="widget bg-depth bg-depth-hover flex items-start gap-3 cursor-pointer border-2 transition-colors"
								class:border-transparent={price.uuid !== selectedPrice?.uuid}
								class:border-primary-500={price.uuid === selectedPrice?.uuid}
								class:opacity-60={price.unavailable}
							>
								<input
									type="radio"
									class="mt-1.5"
									name="price"
									value={price.uuid}
									bind:group={selectedPriceUUID}
									disabled={price.unavailable}
								/>
								<div class="grow min-w-0">
									<PriceDisplay {price} currency={$activeCurrency} currencyCode={product.currency} size="sm" />
									{#if saving !== null}
										<!-- A percentage, not a second money figure for the same price. -->
										<span class="inline-block mt-1 text-xs font-semibold text-green-700 dark:text-green-400">
											<i class="fa-solid fa-arrow-trend-down mr-1" />Save {saving}% against monthly
										</span>
									{/if}
									{#if actionNote(price) !== ''}
										<span class="block mt-1 text-xs text-default">{actionNote(price)}</span>
									{/if}
								</div>
								{#if price.action === 'current'}
									<Badge color="green">Current plan</Badge>
								{/if}
							</label>
						{/each}
					</div>

					<!--
						The way down to the checkout. The steps sit under the description in the left
						column, which on a long page is below the fold from the rail — a customer who
						has chosen a plan then has to hunt for the thing that takes their money.

						It **scrolls and commits nothing**: the control that pays sits at the end of
						the steps beside the terms it is bound by. Hence the arrow and the label
						naming the heading it lands on rather than a bare "Continue", and hence its
						never being disabled — see `fnGoToCheckout`.
					-->
					<!-- Not on the plan already held: there is nothing below to commit, and a "Buy
					     now" over the tier somebody is already paying for is the same claim the
					     purchase form was making. -->
					{#if confirmMode !== 'held'}
						<Button size="lg" class="w-full" on:click={fnGoToCheckout}>
							{jumpLabel}<i class="fa-solid fa-arrow-down ml-2" />
						</Button>
					{/if}

					{#if coupon !== null && coupon.validation.finalAmount !== null}
						<div class="text-sm flex items-baseline gap-2 flex-wrap">
							<span class="text-default line-through">
								<PriceDisplay
									amount={selectedPrice?.amount ?? null}
									currency={$activeCurrency}
									currencyCode={product.currency}
									showInterval={false}
									showTaxNote={false}
									showTrial={false}
									size="sm"
								/>
							</span>
							<span class="text-green-700 dark:text-green-400">
								<PriceDisplay
									amount={coupon.validation.finalAmount}
									currency={$activeCurrency}
									currencyCode={product.currency}
									showInterval={false}
									showTaxNote={false}
									showTrial={false}
									size="sm"
								/>
							</span>
							<span class="text-xs text-default">with {coupon.code}</span>
						</div>
					{/if}

					<!--
						Seats sit directly above Buy, because the total they produce is the number the
						customer is agreeing to — a per-seat unit price beside a Buy button is the one
						figure people reliably read as the cost of the plan.

						Shown on a plan already held as well, because this is where seats are changed
						now. It opens on the team in force rather than on the tier's floor — telling a
						customer with forty users that they have five, on the screen where they decide
						what to pay for, was the reason it used to be hidden here at all.
					-->
					{#if selectedPrice !== null && selectedPrice.isSeatBased && (selectedPrice.action !== 'current' || isSeatChange(selectedPrice))}
						<div class="mb-3">
							<p class="card-label mb-2">
								<i class={`fa-solid ${unitWords.icon} text-[11px]`} />{unitWords.label}
							</p>
							<SeatSelector
								bind:seats
								noun={unitWords.singular}
								nounPlural={unitWords.plural}
								label={unitWords.label}
								seatMin={selectedPrice.seatMin}
								seatMax={selectedPrice.seatMax}
								unitAmount={selectedPrice.amount}
								currency={$activeCurrency}
								disabled={!isOpen}
							/>
						</div>
					{/if}

					<!-- What they hold, so "Change seats" is an offer rather than a bare instruction. -->
					{#if isSeatChange(selectedPrice) && selectedPrice?.heldSeats != null}
						<p class="text-sm text-default mb-3">
							You have {selectedPrice.heldSeats}
							{selectedPrice.heldSeats === 1 ? 'user' : 'users'} on this plan.
						</p>
					{/if}

					<!--
						The money, always: there is no state in which the customer is choosing a
						plan but not being told what it costs. The button that commits sits at the
						end of the steps beside the terms it is bound by, not here — two controls
						that both look like the end of a purchase is how the wrong one gets pressed.
					-->
					<!-- Suppressed on the plan already held: an order summary is what is about to be
					     bought, and it quoted a "Due today" for a renewal that is simply the plan
					     continuing. The price card above already states what they pay, beside the
					     Current plan badge. -->
					{#if confirmMode !== 'held'}
						<OrderSummary
							mode={stepsMode}
							{product}
							price={selectedPrice}
							currency={$activeCurrency}
							{seats}
							unitNoun={unitWords.singular}
							unitNounPlural={unitWords.plural}
							coupon={confirmMode === 'buy' ? coupon : null}
							selectedOptions={confirmedOptions}
							{preview}
							{previewLoading}
							{previewError}
							isBillingChange={isBillingChange(selectedPrice)}
							currentProductName={heldTierName}
							heldSeats={selectedPrice?.heldSeats ?? null}
						/>
					{/if}

					{#if !isOpen}
						<p class="text-xs text-default text-center">
							The store is not open yet, so purchases are not being taken.
						</p>
					{:else if selectedPrice?.unavailable}
						<p class="text-xs text-default text-center">
							This product is not sold in {(selectedPrice.currency || '').toUpperCase()}. Pick another currency
							above.
						</p>
					{/if}

					<!--
						What the purchase commits them to. The security line is **not** here: the
						steps say it beside the button that commits, which is where somebody
						hesitating actually reads it, and two statements of one fact in one column
						is how a rail gets long enough that the figure it exists for scrolls away.
					-->
					<ul class="text-xs text-default space-y-1.5 pt-1 border-t border-widget-color">
						{#if product.type === 'subscription'}
							<li class="flex items-start gap-2">
								<i class="fa-solid fa-rotate-left mt-0.5" />
								<span>Cancel any time from your account; access runs to the end of the period.</span>
							</li>
						{/if}
						{#if product.refundWindowDays !== null && product.refundWindowDays > 0}
							<li class="flex items-start gap-2">
								<i class="fa-solid fa-shield-halved mt-0.5" />
								<span>Refundable within {product.refundWindowDays} days of purchase.</span>
							</li>
						{/if}
					</ul>
				{/if}
			</div>

			{#if selectedPrice !== null}
				<CouponField
					priceUUID={selectedPrice.uuid}
					currencyCode={selectedPrice.currency}
					currency={$activeCurrency}
					isSignedIn={$isAuthorized}
					enabled={!selectedPrice.unavailable && isOpen}
					blockedReason={couponBlockedReason}
					initialCode={arrivingCoupon}
					on:applied={event => {
						coupon = event.detail;
						/*
						 * Whether a code was applied, and **never which code or why not** (§12).
						 * `validateCoupon` answers every failure with one uniform body precisely so a
						 * code cannot be guessed, and recording the attempted string here would put
						 * that oracle back in a table an operator reads.
						 */
						track('coupon_attempted', { productSlug: slug, properties: { applied: true } });
					}}
					on:cleared={() => {
						coupon = null;
						track('coupon_attempted', { productSlug: slug, properties: { applied: false } });
					}}
				/>
			{/if}

			{#if $configurationStore?.supportEmail}
				<p class="text-xs text-default text-center">
					Questions? <a class="text-primary-500 hover:underline" href={`mailto:${$configurationStore.supportEmail}`}>
						{$configurationStore.supportEmail}
					</a>
				</p>
			{/if}
		</div>
	</div>
{/if}
